import { useMemo } from 'react';
import { isImportActive } from '../services/externalEmrImportStatus';
import {
  cancelPatientExternalEmrImport,
  historyPending,
  rendersPending,
  externalEmrCardStatus,
  type ExternalEmrPatientState,
  getPatientExternalEmrImport,
  normalizePatientImportStatus,
  refreshExternalEmrPatient,
  startExternalEmrPatientImports,
  type PatientExternalEmrImportStartResult,
  type ExternalEmrImportStatus,
} from '../services/externalEmrPatients';
import { IMPORT_POLL_INTERVAL_MS } from './useExternalEmrImport';
import { createImportStatusStore } from './importStatusStore';

/**
 * A queued card re-reads more slowly than a running one. A bulk start can
 * queue a whole page of cards, and a queue position moves slowly; fifty cards
 * at the running pace would be ten requests a second.
 */
export const QUEUED_POLL_INTERVAL_MS = 15_000;

/**
 * Print renders run as their own queue rows after the chart lands and take
 * many minutes. A watched card or chart re-reads them once a minute while
 * they only wait in the queue.
 */
export const RENDERS_POLL_INTERVAL_MS = 60_000;

/**
 * A render row that is running files its documents within a minute or two.
 * Re-read at the queued-card pace then, so "Documents still arriving" clears
 * soon after the last one files instead of up to a minute later -- long
 * enough that people reload to make it go away.
 */
export const RENDERS_RUNNING_POLL_INTERVAL_MS = QUEUED_POLL_INTERVAL_MS;

const patientImportStore = createImportStatusStore<ExternalEmrImportStatus>({
  fetch: getPatientExternalEmrImport,
  empty: normalizePatientImportStatus(null),
  pollIntervalMs: status => {
    if (status.state === 'queued') return QUEUED_POLL_INTERVAL_MS;
    if (status.state === 'running') return IMPORT_POLL_INTERVAL_MS;
    if ((status.renders?.running ?? 0) > 0)
      return RENDERS_RUNNING_POLL_INTERVAL_MS;
    return RENDERS_POLL_INTERVAL_MS;
  },
  // Earlier episodes arriving behind a recent-first chart wait at bulk
  // priority, so they re-read at the renders' slow pace.
  shouldPoll: status =>
    isImportActive(status.state) ||
    rendersPending(status) ||
    historyPending(status),
  failureLog: 'Failed to load the patient External EMR import status',
});

/**
 * One patient's External EMR import, shared by the Patient Manager card and
 * the chart page. `seed` is the status the list or patient read already
 * carries, so a card makes no request until it needs to poll.
 */
export function usePatientExternalEmrImport(
  patientId: string | null | undefined,
  {
    enabled = true,
    seed = null,
    seedAt = null,
  }: {
    enabled?: boolean;
    seed?: ExternalEmrImportStatus | null;
    /** When the list request carrying `seed` began (ms); null trusts it. */
    seedAt?: number | null;
  } = {}
) {
  return patientImportStore.useStatus(patientId, { enabled, seed, seedAt });
}

/**
 * The live card status for one patient: the list's stored state, overtaken by
 * the import poll. Polls only while the import is queued or running.
 */
export function useExternalEmrCardStatus(
  patientId: string,
  external: ExternalEmrPatientState,
  seedAt: number | null = null
) {
  // A stable seed: the list's status, or "none" for a shell never started.
  // Seeding "none" means a quiet card makes no request at all.
  const seed = useMemo(
    () => normalizePatientImportStatus(external.import),
    [external.import]
  );
  const { status: live } = usePatientExternalEmrImport(patientId, {
    seed,
    seedAt,
  });
  const status = externalEmrCardStatus(external, live);
  // A shell whose import just completed is a chart now, before the list
  // reload says so.
  const isShell = external.state === 'shell' && status !== 'imported';
  return { live, status, isShell };
}

/**
 * Start imports for these patients. A row that started becomes queued in the
 * store at once, so its card shows Queued before the next read. Throws when
 * the request itself fails; per-row refusals come back in the rows.
 */
export async function startPatientExternalEmrImports(
  patientIds: string[]
): Promise<PatientExternalEmrImportStartResult[]> {
  const rows = await startExternalEmrPatientImports(patientIds);
  for (const row of rows) {
    if (row.refused_reason) continue;
    // A start on a live row joins it and answers queued or running; a
    // failed row reopens as queued. Show what the server said.
    patientImportStore.publish(
      row.patient_id,
      normalizePatientImportStatus({
        state: row.status === 'running' ? 'running' : 'queued',
      })
    );
    // The queue position comes from the status read.
    void patientImportStore.refresh(row.patient_id);
  }
  return rows;
}

/**
 * Tell the shared import store a refresh was queued or is running, so every
 * surface that reads the patient (the chart bar, census cards) polls until it
 * settles.
 */
function publishPatientExternalEmrRefreshStart(
  row: Pick<
    PatientExternalEmrImportStartResult,
    'patient_id' | 'status' | 'refused_reason'
  >
): void {
  if (row.refused_reason) return;
  const current = patientImportStore.get(row.patient_id);
  patientImportStore.publish(
    row.patient_id,
    normalizePatientImportStatus({
      state: row.status === 'running' ? 'running' : 'queued',
      refresh: true,
      last_refreshed_at:
        current?.last_refreshed_at ??
        (current?.state === 'completed' && current.refresh
          ? current.finished_at
          : null),
    })
  );
  void patientImportStore.refresh(row.patient_id);
}

/**
 * Refresh one imported chart from the External EMR. A row that started
 * shows as queued or running at once, marked as a refresh, so the card
 * reads Refreshing before the next read. Throws when the request itself
 * fails; a refusal comes back in the row.
 */
export async function refreshPatientExternalEmrImport(
  patientId: string
): Promise<PatientExternalEmrImportStartResult> {
  const row = await refreshExternalEmrPatient(patientId);
  publishPatientExternalEmrRefreshStart(row);
  return row;
}

/** Cancel a queued import. Throws on failure. */
export async function cancelPatientExternalEmrImportAndPublish(
  patientId: string
): Promise<ExternalEmrImportStatus> {
  const status = await cancelPatientExternalEmrImport(patientId);
  patientImportStore.publish(patientId, status);
  return status;
}

/** The status a card is showing now, for bulk actions over a selection. */
export function getPatientExternalEmrImportStatus(
  patientId: string
): ExternalEmrImportStatus | null {
  return patientImportStore.get(patientId);
}

/** Tests only: forget every patient and stop every timer. */
export function resetPatientExternalEmrImportStore() {
  patientImportStore.reset();
}
